import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Check, Loader2, Plus, School, Settings, UserMinus, UserPlus, Users, X } from "lucide-react";
import { NotificationModal } from "@/components/modals";
import { useNotification } from "@/contexts/NotificationContext";
import { api, del, errorMessage, patch, post } from "@/lib/api";
import "./CourseEnrollDialog.css";

interface Participant {
  userId: string;
  ugpId?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  teamName?: string;
  role: string;
}

type InstructorKit = {
  proposeActivities: boolean;
  editActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
  studentProgressChat: boolean;
};

type EmailKit = Record<string, boolean>;

interface EnrollmentRow {
  id: string;
  uisUserId: string;
  uisUgpId: string;
  role: string;
  permissions?: (Partial<InstructorKit> & EmailKit) | null;
  person?: Participant | null;
}

export type EnrollTarget = {
  courseId: string;
  title: string;
  schoolId: string;
  schoolName?: string;
};

type EnrollTab = "learner" | "instructor";

const INSTRUCTOR_ROSTER_ROLES = new Set(["teacher", "curriculumDeveloper"]);

const DEFAULT_KIT: InstructorKit = {
  proposeActivities: true,
  editActivities: true,
  gradeActivities: true,
  overrideActivities: true,
  studentProgressChat: true,
};

const KIT_OPTIONS: { key: keyof InstructorKit; label: string; subtext: string }[] = [
  {
    key: "proposeActivities",
    label: "Propose",
    subtext: "Propose changes to this course. Needs Teach Enrolled propose.",
  },
  {
    key: "editActivities",
    label: "Edit",
    subtext: "Live-edit this course. Needs Teach Enrolled edit.",
  },
  {
    key: "gradeActivities",
    label: "Grade",
    subtext: "View and mark student work. Needs Teach Enrolled grade.",
  },
  {
    key: "overrideActivities",
    label: "Override",
    subtext: "Extra attempts, mute, or late. Needs Teach Enrolled override.",
  },
  {
    key: "studentProgressChat",
    label: "Student chat",
    subtext: "Message the student on their work. This course only — not a Teach Enrolled verb.",
  },
];

/**
 * Email switches on this class enrollment (EMAIL-NOTIFICATIONS.md). Not
 * capabilities. Omitted on the row means the role default: instructors on,
 * students off. The row is the override.
 */
type EmailOption = { key: string; label: string; subtext: string };

const INSTRUCTOR_EMAIL_OPTIONS: EmailOption[] = [
  {
    key: "emailCourseAnnouncements",
    label: "Class announcements",
    subtext: "Email when another teacher posts an announcement to this class.",
  },
  {
    key: "emailStudentWorkChat",
    label: "Student replies",
    subtext: "Email when a student, parent, or counselor writes on a student's work.",
  },
  {
    key: "emailStaffWorkChat",
    label: "Staff channel",
    subtext: "Email when another teacher writes in the staff room on a student's work.",
  },
];

const LEARNER_EMAIL_OPTIONS: EmailOption[] = [
  {
    key: "emailCourseAnnouncements",
    label: "Class announcements",
    subtext: "Email this student when a teacher posts an announcement to this class.",
  },
  {
    key: "emailWorkChat",
    label: "Coursework chat",
    subtext: "Email this student when a teacher, parent, or counselor writes on their work.",
  },
];

function emailOptionsFor(role: string) {
  return role === "instructor" ? INSTRUCTOR_EMAIL_OPTIONS : LEARNER_EMAIL_OPTIONS;
}

/** Row value for each email key, falling back to the role default. */
function emailKitFor(role: string, permissions?: EmailKit | null): EmailKit {
  const fallback = role === "instructor";
  const out: EmailKit = {};
  for (const opt of emailOptionsFor(role)) {
    const value = permissions?.[opt.key];
    out[opt.key] = typeof value === "boolean" ? value : fallback;
  }
  return out;
}

const KIT_CEILING_HINT =
  "These only narrow UIS Teach Enrolled for this course, so the same teacher can have different access on different copies — still limited by what Teach Enrolled unlocks for them.";

function displayName(p: Participant) {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || p.email || "Unnamed";
}

function rowLabel(row: EnrollmentRow) {
  return row.person ? displayName(row.person) : row.uisUserId;
}

function mergeKit(defaults: InstructorKit, override?: Partial<InstructorKit> | null): InstructorKit {
  return {
    proposeActivities:
      override?.proposeActivities != null ? override.proposeActivities !== false : defaults.proposeActivities,
    editActivities:
      override?.editActivities != null ? override.editActivities !== false : defaults.editActivities,
    gradeActivities:
      override?.gradeActivities != null ? override.gradeActivities !== false : defaults.gradeActivities,
    overrideActivities:
      override?.overrideActivities != null
        ? override.overrideActivities !== false
        : defaults.overrideActivities,
    studentProgressChat:
      override?.studentProgressChat != null
        ? override.studentProgressChat !== false
        : defaults.studentProgressChat,
  };
}

/** Existing instructor row: missing studentProgressChat is off, not inherited on. */
function mergeEnrollmentKit(defaults: InstructorKit, override?: Partial<InstructorKit> | null): InstructorKit {
  return {
    ...mergeKit(defaults, override),
    studentProgressChat: override?.studentProgressChat === true,
  };
}

function EnrollLoading({ label }: { label: string }) {
  return (
    <p className="al-enroll-loading" role="status">
      <Loader2 size={14} className="al-enroll-spin" aria-hidden="true" />
      {label}
    </p>
  );
}

function KitCards({
  kit,
  disabled,
  onChange,
}: {
  kit: InstructorKit;
  disabled?: boolean;
  onChange: (next: InstructorKit) => void;
}) {
  return (
    <div className="al-enroll-kit-cards" role="group" aria-label="Instructor permissions">
      {KIT_OPTIONS.map((opt) => {
        const on = kit[opt.key] === true;
        return (
          <button
            key={opt.key}
            type="button"
            className={`al-enroll-kit-card${on ? " al-enroll-kit-card--on" : ""}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange({ ...kit, [opt.key]: !on })}
          >
            <span className="al-enroll-kit-card-head">
              <span className="al-enroll-kit-card-title">{opt.label}</span>
              {on ? (
                <span className="al-enroll-kit-card-check" aria-hidden="true">
                  <Check size={11} strokeWidth={2.75} />
                </span>
              ) : null}
            </span>
            <span className="al-enroll-kit-card-sub">{opt.subtext}</span>
          </button>
        );
      })}
      <div className="al-enroll-kit-card al-enroll-kit-card--on al-enroll-kit-card--locked">
        <span className="al-enroll-kit-card-head">
          <span className="al-enroll-kit-card-title">Staff chat & notes</span>
          <span className="al-enroll-kit-card-check" aria-hidden="true">
            <Check size={11} strokeWidth={2.75} />
          </span>
        </span>
        <span className="al-enroll-kit-card-sub">
          Staff room and personal notes on a student's progress — a reading with no paper included. Always on — not a live permission.
        </span>
      </div>
    </div>
  );
}

function EmailCards({
  role,
  kit,
  disabled,
  onChange,
}: {
  role: string;
  kit: EmailKit;
  disabled?: boolean;
  onChange: (key: string, on: boolean) => void;
}) {
  return (
    <div className="al-enroll-email">
      <p className="al-enroll-email-head">Email</p>
      <div className="al-enroll-kit-cards" role="group" aria-label="Email for this class">
        {emailOptionsFor(role).map((opt) => {
          const on = kit[opt.key] === true;
          return (
            <button
              key={opt.key}
              type="button"
              className={`al-enroll-kit-card${on ? " al-enroll-kit-card--on" : ""}`}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onChange(opt.key, !on)}
            >
              <span className="al-enroll-kit-card-head">
                <span className="al-enroll-kit-card-title">{opt.label}</span>
                {on ? (
                  <span className="al-enroll-kit-card-check" aria-hidden="true">
                    <Check size={11} strokeWidth={2.75} />
                  </span>
                ) : null}
              </span>
              <span className="al-enroll-kit-card-sub">{opt.subtext}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CourseEnrollDialog({
  target,
  onClose,
}: {
  target: EnrollTarget | null;
  onClose: () => void;
}) {
  const { toast, notice } = useNotification();
  const [tab, setTab] = useState<EnrollTab>("learner");
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [courseDefaults, setCourseDefaults] = useState<InstructorKit>(DEFAULT_KIT);
  const [addKit, setAddKit] = useState<InstructorKit>(DEFAULT_KIT);
  const [addEmail, setAddEmail] = useState<EmailKit>(() => emailKitFor("learner", null));
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUgpIds, setSelectedUgpIds] = useState<Set<string>>(new Set());
  const [enrolling, setEnrolling] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [savingKitId, setSavingKitId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [kitTargetId, setKitTargetId] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<EnrollmentRow | null>(null);
  const [canAddEnrollments, setCanAddEnrollments] = useState(true);
  const [enrollClosedHint, setEnrollClosedHint] = useState<string | null>(null);

  const load = useCallback(
    async (courseId: string, schoolId: string) => {
      setLoading(true);
      try {
        const [enrollRes, peopleRes] = await Promise.all([
          api<{
            success: boolean;
            data?: {
              enrollments?: EnrollmentRow[];
              course?: {
                instructorDefaults?: Partial<InstructorKit>;
                canEnroll?: boolean;
                withdrawn?: boolean;
              };
            };
          }>(`/api/courses/${encodeURIComponent(courseId)}/enrollments`),
          api<{ success: boolean; data?: { participants?: Participant[] } }>(
            `/api/schools/${encodeURIComponent(schoolId)}/participants`,
          ),
        ]);
        const defaults = mergeKit(DEFAULT_KIT, enrollRes.data?.course?.instructorDefaults || null);
        setCourseDefaults(defaults);
        setEnrollments(Array.isArray(enrollRes.data?.enrollments) ? enrollRes.data.enrollments : []);
        setParticipants(Array.isArray(peopleRes.data?.participants) ? peopleRes.data.participants : []);
        const withdrawn = Boolean(enrollRes.data?.course?.withdrawn);
        const enrollOpen = enrollRes.data?.course?.canEnroll !== false;
        setCanAddEnrollments(!withdrawn && enrollOpen);
        setEnrollClosedHint(
          withdrawn
            ? "This course was withdrawn from the school. Existing people keep access; nobody new can be enrolled until it is restored."
            : enrollOpen
              ? null
              : "Enrollment is closed for this course. Existing people keep access.",
        );
      } catch (err) {
        setEnrollments([]);
        setParticipants([]);
        setCanAddEnrollments(false);
        setEnrollClosedHint(null);
        notice({
          type: "error",
          title: "Could not load enrollments",
          subtext: errorMessage(err),
          duration: 7000,
        });
      } finally {
        setLoading(false);
      }
    },
    [notice],
  );

  useEffect(() => {
    if (!target) {
      setEnrollments([]);
      setParticipants([]);
      setSelectedUgpIds(new Set());
      setTab("learner");
      setAddKit(DEFAULT_KIT);
      setShowAdd(false);
      setKitTargetId(null);
      setPendingRevoke(null);
      setCanAddEnrollments(true);
      setEnrollClosedHint(null);
      return;
    }
    setSelectedUgpIds(new Set());
    setAddKit(DEFAULT_KIT);
    setShowAdd(false);
    setKitTargetId(null);
    setPendingRevoke(null);
    void load(target.courseId, target.schoolId);
  }, [target, load]);

  const enrolledUgpIds = useMemo(
    () => new Set(enrollments.map((e) => String(e.uisUgpId))),
    [enrollments],
  );

  const listed = useMemo(
    () => enrollments.filter((row) => (row.role || "learner") === tab),
    [enrollments, tab],
  );

  const kitRow = useMemo(
    () => enrollments.find((row) => row.id === kitTargetId) || null,
    [enrollments, kitTargetId],
  );

  const candidates = useMemo(() => {
    const wantRole = tab === "instructor" ? INSTRUCTOR_ROSTER_ROLES : new Set(["student"]);
    return participants.filter(
      (p) => wantRole.has(p.role) && p.ugpId && !enrolledUgpIds.has(String(p.ugpId)),
    );
  }, [participants, enrolledUgpIds, tab]);

  const toggleSelect = (ugpId: string) => {
    const id = String(ugpId);
    setSelectedUgpIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const switchTab = (next: EnrollTab) => {
    setTab(next);
    setSelectedUgpIds(new Set());
    setShowAdd(false);
    setKitTargetId(null);
    setPendingRevoke(null);
  };

  const openAdd = () => {
    if (!canAddEnrollments) return;
    setSelectedUgpIds(new Set());
    if (tab === "instructor") setAddKit(courseDefaults);
    setAddEmail(emailKitFor(tab, null));
    setShowAdd(true);
  };

  const nestedOpen = showAdd || Boolean(kitRow) || Boolean(pendingRevoke);

  const closeAdd = () => {
    if (enrolling) return;
    setShowAdd(false);
    setSelectedUgpIds(new Set());
  };

  const handleEnroll = async () => {
    if (!target || !canAddEnrollments || selectedUgpIds.size === 0) return;
    const picks = participants.filter((p) => p.ugpId && selectedUgpIds.has(String(p.ugpId)));
    if (!picks.length) return;
    setEnrolling(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const p of picks) {
        try {
          await post(`/api/courses/${encodeURIComponent(target.courseId)}/enrollments`, {
            uisUserId: p.userId,
            uisUgpId: p.ugpId,
            role: tab,
            permissions: tab === "instructor" ? { ...addKit, ...addEmail } : { ...addEmail },
          });
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      const one = tab === "instructor" ? "Instructor enrolled" : "Student enrolled";
      const many =
        tab === "instructor" ? `${ok} instructors enrolled` : `${ok} students enrolled`;
      if (ok) toast.success(ok === 1 ? one : many);
      if (fail) {
        notice({
          type: "warning",
          title: "Some enrollments failed",
          subtext: `${fail} could not be enrolled.`,
          duration: 7000,
        });
      }
      setSelectedUgpIds(new Set());
      if (ok) setShowAdd(false);
      await load(target.courseId, target.schoolId);
    } finally {
      setEnrolling(false);
    }
  };

  const handleRevoke = async (enrollment: EnrollmentRow) => {
    if (!target) return;
    setRevokingId(enrollment.id);
    try {
      await del(
        `/api/courses/${encodeURIComponent(target.courseId)}/enrollments/${encodeURIComponent(enrollment.id)}`,
      );
      toast.success("Enrollment removed");
      setPendingRevoke(null);
      if (kitTargetId === enrollment.id) setKitTargetId(null);
      await load(target.courseId, target.schoolId);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not remove enrollment",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setRevokingId(null);
    }
  };

  const handleKitChange = async (enrollment: EnrollmentRow, next: InstructorKit) => {
    if (!target) return;
    const previous = mergeEnrollmentKit(courseDefaults, enrollment.permissions);
    setEnrollments((rows) =>
      rows.map((row) =>
        row.id === enrollment.id ? { ...row, permissions: { ...(row.permissions || {}), ...next } } : row,
      ),
    );
    setSavingKitId(enrollment.id);
    try {
      await patch(
        `/api/courses/${encodeURIComponent(target.courseId)}/enrollments/${encodeURIComponent(enrollment.id)}`,
        { permissions: next },
      );
    } catch (err) {
      setEnrollments((rows) =>
        rows.map((row) => (row.id === enrollment.id ? { ...row, permissions: previous } : row)),
      );
      notice({
        type: "error",
        title: "Could not update instructor permissions",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSavingKitId(null);
    }
  };

  /** One email switch on one row. An instructor save sends the whole teach kit too (the API requires it). */
  const handleEmailChange = async (enrollment: EnrollmentRow, key: string, on: boolean) => {
    if (!target) return;
    const role = enrollment.role === "instructor" ? "instructor" : "learner";
    const before = enrollment.permissions || null;
    const permissions =
      role === "instructor"
        ? {
            ...mergeEnrollmentKit(courseDefaults, enrollment.permissions),
            ...emailKitFor(role, enrollment.permissions),
            [key]: on,
          }
        : { [key]: on };
    setEnrollments((rows) =>
      rows.map((row) =>
        row.id === enrollment.id ? { ...row, permissions: { ...(row.permissions || {}), [key]: on } } : row,
      ),
    );
    setSavingKitId(enrollment.id);
    try {
      await patch(
        `/api/courses/${encodeURIComponent(target.courseId)}/enrollments/${encodeURIComponent(enrollment.id)}`,
        { permissions },
      );
    } catch (err) {
      setEnrollments((rows) => rows.map((row) => (row.id === enrollment.id ? { ...row, permissions: before } : row)));
      notice({
        type: "error",
        title: "Could not update email settings",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSavingKitId(null);
    }
  };

  const noun = tab === "instructor" ? "instructors" : "students";
  const busy = enrolling || Boolean(revokingId) || Boolean(savingKitId);

  return (
    <>
    <NotificationModal
      isOpen={Boolean(target)}
      type="neutral"
      icon={<Users />}
      title="Enroll in a Course"
      message="People on this school’s roster. Enrollment is for this school’s course copy only."
      overlay
      overlayClose={!busy && !nestedOpen}
      escapeClose={!nestedOpen}
      showOkButton={false}
      align="center"
      iconSize="2.25rem"
      titleSize="1.05rem"
      onClose={onClose}
    >
      <div className="al-enroll">
        {target ? (
          <div className="al-enroll-badges">
            <span className="al-enroll-badge" title={target.title}>
              <BookOpen size={12} aria-hidden="true" />
              <span>{target.title}</span>
            </span>
            {target.schoolName ? (
              <span className="al-enroll-badge" title={target.schoolName}>
                <School size={12} aria-hidden="true" />
                <span>{target.schoolName}</span>
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="al-enroll-tabs" role="tablist" aria-label="Enrollment type">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "learner"}
            className={`al-enroll-badge al-enroll-badge--clickable${tab === "learner" ? " al-enroll-badge--active" : ""}`}
            onClick={() => switchTab("learner")}
          >
            Students
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "instructor"}
            className={`al-enroll-badge al-enroll-badge--clickable${tab === "instructor" ? " al-enroll-badge--active" : ""}`}
            onClick={() => switchTab("instructor")}
          >
            Instructors
          </button>
        </div>

        <section className="al-enroll-section">
          <div className="al-enroll-section-head">
            <h3>Enrolled ({listed.length})</h3>
            {canAddEnrollments ? (
              <button
                type="button"
                className="al-enroll-add"
                title={`Add ${noun}`}
                aria-label={`Add ${noun}`}
                aria-haspopup="dialog"
                aria-expanded={showAdd}
                disabled={busy || loading}
                onClick={openAdd}
              >
                <Plus size={15} aria-hidden="true" />
              </button>
            ) : null}
          </div>
          {enrollClosedHint ? <p className="al-enroll-hint">{enrollClosedHint}</p> : null}
          {loading ? (
            <EnrollLoading label="Loading enrollments…" />
          ) : listed.length === 0 ? (
            <p className="al-enroll-hint">No {noun} enrolled yet.</p>
          ) : (
            <ul className="al-enroll-list">
              {listed.map((row) => {
                const name = rowLabel(row);
                return (
                  <li key={row.id} className="al-enroll-row">
                    <div className="al-enroll-row-copy">
                      <div className="al-enroll-name">{name}</div>
                      <div className="al-enroll-meta">{[row.person?.email, row.role].filter(Boolean).join(" · ")}</div>
                    </div>
                    <div className="al-enroll-row-actions">
                      <button
                        type="button"
                        className="al-enroll-icon"
                        title={tab === "instructor" ? "Permissions" : "Email settings"}
                        aria-label={`${tab === "instructor" ? "Permissions" : "Email settings"} for ${name}`}
                        disabled={busy}
                        onClick={() => setKitTargetId(row.id)}
                      >
                        <Settings size={15} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="al-enroll-icon al-enroll-icon--remove"
                        title="Double-click to remove"
                        aria-label={`Remove ${name} (double-click to confirm)`}
                        disabled={busy}
                        onDoubleClick={() => setPendingRevoke(row)}
                      >
                        {revokingId === row.id ? (
                          <Loader2 size={14} className="al-enroll-spin" aria-hidden="true" />
                        ) : (
                          <X size={14} strokeWidth={1.75} aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="al-enroll-actions">
          <button type="button" className="al-enroll-btn al-enroll-btn--ghost" onClick={onClose} disabled={enrolling}>
            Close
          </button>
        </div>
      </div>
    </NotificationModal>

      <NotificationModal
        isOpen={showAdd}
        type="neutral"
        icon={<UserPlus />}
        title={tab === "instructor" ? "Add instructors" : "Add students"}
        message={
          tab === "instructor"
            ? "These are the default settings. Whatever they are right now will apply to the people you enroll right now."
            : target?.schoolName
              ? `From ${target.schoolName}. Already enrolled ${noun} are hidden.`
              : "People on this school’s roster who are not enrolled yet."
        }
        overlay
        overlayClose={!enrolling}
        escapeClose={!enrolling}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1250}
        style={{ width: "min(26rem, calc(100vw - 2rem))", maxHeight: "min(90vh, 40rem)" }}
        onClose={closeAdd}
      >
        <div className="al-enroll al-enroll--picker">
          {tab === "instructor" ? (
            <>
              <KitCards kit={addKit} disabled={enrolling} onChange={setAddKit} />
              <EmailCards
                role="instructor"
                kit={addEmail}
                disabled={enrolling}
                onChange={(key, on) => setAddEmail((prev) => ({ ...prev, [key]: on }))}
              />
              <p className="al-enroll-hint">
                {target?.schoolName
                  ? `From ${target.schoolName}. Already enrolled instructors are hidden.`
                  : "Already enrolled instructors are hidden."}{" "}
                {KIT_CEILING_HINT}
              </p>
            </>
          ) : (
            <>
              <EmailCards
                role="learner"
                kit={addEmail}
                disabled={enrolling}
                onChange={(key, on) => setAddEmail((prev) => ({ ...prev, [key]: on }))}
              />
              <p className="al-enroll-hint">Off by default. Students are only emailed for what is turned on here.</p>
            </>
          )}
          {loading ? (
            <EnrollLoading label={tab === "instructor" ? "Loading instructors…" : "Loading students…"} />
          ) : candidates.length === 0 ? (
            <p className="al-enroll-hint">
              {tab === "instructor"
                ? "No more teachers on this school to enroll (or none with a UGP)."
                : "No more students on this school to enroll (or none with a UGP)."}
            </p>
          ) : (
            <ul className="al-enroll-list">
              {candidates.map((p) => {
                const ugpId = String(p.ugpId);
                return (
                  <li key={ugpId} className="al-enroll-row">
                    <label className="al-enroll-pick">
                      <input
                        type="checkbox"
                        checked={selectedUgpIds.has(ugpId)}
                        disabled={enrolling}
                        onChange={() => toggleSelect(ugpId)}
                      />
                      <span>
                        <span className="al-enroll-name">{displayName(p)}</span>
                        <span className="al-enroll-meta">{[p.email, p.teamName].filter(Boolean).join(" · ")}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="al-enroll-actions">
            <button
              type="button"
              className="al-enroll-btn al-enroll-btn--ghost"
              onClick={closeAdd}
              disabled={enrolling}
            >
              Cancel
            </button>
            <button
              type="button"
              className="al-enroll-btn"
              disabled={enrolling || selectedUgpIds.size === 0}
              onClick={() => void handleEnroll()}
            >
              {enrolling ? "Enrolling…" : selectedUgpIds.size ? `Enroll ${selectedUgpIds.size}` : "Enroll"}
            </button>
          </div>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(kitRow)}
        type="neutral"
        icon={<Settings />}
        title={kitRow ? rowLabel(kitRow) : "Permissions"}
        message={
          kitRow && kitRow.role !== "instructor"
            ? "Email for this class only. Off by default for students."
            : KIT_CEILING_HINT
        }
        overlay
        overlayClose={!savingKitId}
        escapeClose={!savingKitId}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1260}
        style={{ width: "min(26rem, 96vw)" }}
        onClose={() => {
          if (!savingKitId) setKitTargetId(null);
        }}
      >
        {kitRow ? (
          <div className="al-enroll">
            {kitRow.role === "instructor" ? (
              <KitCards
                kit={mergeEnrollmentKit(courseDefaults, kitRow.permissions)}
                disabled={busy}
                onChange={(next) => void handleKitChange(kitRow, next)}
              />
            ) : null}
            <EmailCards
              role={kitRow.role === "instructor" ? "instructor" : "learner"}
              kit={emailKitFor(kitRow.role === "instructor" ? "instructor" : "learner", kitRow.permissions)}
              disabled={busy}
              onChange={(key, on) => void handleEmailChange(kitRow, key, on)}
            />
            <div className="al-enroll-actions">
              <button
                type="button"
                className="al-enroll-btn al-enroll-btn--ghost"
                onClick={() => setKitTargetId(null)}
                disabled={Boolean(savingKitId)}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(pendingRevoke)}
        type="warning"
        icon={<UserMinus />}
        title="Remove from this course?"
        message={
          pendingRevoke
            ? `${rowLabel(pendingRevoke)} will lose access. They can be enrolled again later.`
            : null
        }
        overlay
        overlayClose={!revokingId}
        escapeClose={!revokingId}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        onClose={() => {
          if (!revokingId) setPendingRevoke(null);
        }}
      >
        <div className="al-enroll-actions">
          <button
            type="button"
            className="al-enroll-btn al-enroll-btn--ghost"
            disabled={Boolean(revokingId)}
            onClick={() => setPendingRevoke(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="al-enroll-btn al-enroll-btn--danger"
            disabled={Boolean(revokingId)}
            onClick={() => {
              if (pendingRevoke) void handleRevoke(pendingRevoke);
            }}
          >
            {revokingId ? "Removing…" : "Remove"}
          </button>
        </div>
      </NotificationModal>
    </>
  );
}
